<template>
  <section>
    <!-- SB-02 -->
    <div
      id="sb-02"
      v-if="$store.state.currentLocation >= 2"
    >
      <banner-full-width headerText="Risk assessment"></banner-full-width>
      <content-block>
        <!-- 5-01-00 -->
        <!-- <banner-full-width headerText=""></banner-full-width> -->
        <!-- <h1 class="animated infinite bounce delay-2s">Example</h1> -->
        <h2 class="content-block__header content-block__header--red">Up next</h2>
        <p>In this section, we will examine the rowing environment and assess risks that may be present.</p>
        <p>Assessing our environment helps us make sound decisions to mitigate those risks.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="3"
          link="sb-03"
        >
          Show me how
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-01 -->
    <div
      id="sb-03"
      v-if="$store.state.currentLocation >= 3"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Assess risk</h2>
        <p>Risk assessment and decision-making are essential activities that we practice throughout our lives. As coaches, club administrators, board members, athletes or umpires, we all need to assess the risks inherent to our activities. It is how we keep ourselves safe.</p>
        <p>Risk assessment involves three actions.</p>
        <div class="row">
          <div class="col-sm bg-red text-center p-4 m-3">
            <img
              src="@/assets/img/_content/03-1.svg"
              class="img-fluid w-50 mb-4"
              alt="1"
              srcset=""
            >
            <p><strong>Identify risks involved with an activity.</strong></p>
            <p>What could go wrong?</p>
          </div>
          <div class="col-sm bg-red text-center p-4 m-3">
            <img
              src="@/assets/img/_content/03-2.svg"
              class="img-fluid w-50 mb-4"
              alt="2"
              srcset=""
            >
            <p><strong>Analyze those risks.</strong></p>
            <p>How likely are they to happen? How severe are the consequences?</p>
          </div>
          <div class="col-sm bg-red text-center p-4 m-3">
            <img
              src="@/assets/img/_content/03-3.svg"
              class="img-fluid w-50 mb-4"
              alt="3"
              srcset=""
            >
            <p><strong>Plan the treatment for each risk.</strong></p>
            <p>What are you going to do?</p>
          </div>
        </div>
        <p>Capsizing is a risk that may have life-threatening consequences. Swimming lessons, wearing a personal flotation device (PFD), and rowing instruction and practice may reduce this risk and its undesirable consequences.</p>
        <p>If the consequences of risk are severe and the likelihood of it happening is high, we should avoid this risk by not engaging in the activity altogether. </p>
        <p>That’s why we do not go on the water in the middle of a storm or when one is about to break, we do not let novice rowers go out on the water unattended, and we do not hold regattas without umpires and safety boats.</p>
        <p class="text-key-take">You are responsible for your own safety and for not putting others in harm’s way.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="4"
          link="sb-04"
        >
          Safety first
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-04 -->
    <div
      id="sb-04"
      class="bg-img bg-img--01"
      v-if="$store.state.currentLocation >= 4"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Mitigate the risks of rowing: Your responsibility</h2>
        <p>Numerous risks are associated with rowing. </p>
        <p>RCA, Provincial Rowing Associations and rowing clubs have taken actions to reduce rowing risks. Clubs are strongly encouraged to regularly review safety protocols with members.</p>
        <p>The federal government has developed the Safe Boating Guide, Vessel Operation Restriction Regulations, and requirements for Pleasure Craft Operator Cards (only required for powerboats).</p>
        <p>RCA has developed Safety Guidelines, an Event Sanction Policy, a Regatta Manual, and other policies and requirements to mitigate risk in our sport. </p>
        <p>Throughout this course, you’ll see these resources mentioned frequently. It is your responsibility to review them in preparation for your rowing activities.</p>
        <p></p>
        <button-sequenced
          class="btn-nav--red"
          :loc="5"
          link="sb-05"
        >
          I’ll review these resources
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-01 -->
    <div
      id="sb-05"
      v-if="$store.state.currentLocation >= 5"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Be aware and tell others</h2>
        <p>You should be familiar with the rules, regulations, guidelines and protocols that are in place because you’ll need to apply them where appropriate. </p>
        <p>A great way to be prepared to respond to changing situations while rowing is by being <span class="text-red">situationally aware</span>. This means knowing where your shell is in reference to others. Your situational awareness can help you prevent collisions and avoid interference with your safe movement on the water.</p>
        <p>Here are some tips to prepare for a safe experience on the water.</p>
        <div class="row">
          <div class="col-md-6 col-sm-4">
            <ul class="grid-list">
              <li>Plan ahead if you’re going without a coach or safety boat.</li>
              <li>Bring PFDs, navigation lights and safety equipment. </li>
              <li>Check the weather forecast for your area.</li>
            </ul>
          </div>
          <div class="col-md-6 col-sm-4">
            <ul class="grid-list grid-list--border-left">
              <li>Use your club’s <span class="text-red">Out/In Log</span>, if there is one.</li>
              <li>Let somebody know that you are going on the water, where you are planning to go, and when you plan to return. Let them know once you have returned. </li>
            </ul>
          </div>
        </div>
        <p>If you see something happening on the water that is likely to cause harm to you or someone else, report it as quickly as possible and watch until the situation is resolved.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="6"
          link="sb-06"
        >
          Sound advice
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-06 -->
    <div
      id="sb-06"
      v-if="$store.state.currentLocation >= 6"
    >
      <content-block class="quiz-bg">
        <h2 class="content-block__header content-block__header">Storm coming</h2>
        <quiz-radio
          :questions="q01"
          @quiz-complete="
                $store.dispatch('showNext', { n: 7, el: '#menu' })
              "
        ></quiz-radio>
        <!-- <button-sequenced class="btn-nav--red" :loc="0" link="menu">
          Let’s go
        </button-sequenced> -->
      </content-block>
    </div>
    <footer
      class="course-menu"
      id="menu"
      v-if="$store.state.currentLocation >= 7"
    >
      <h3>Course Menu</h3>
      <cac-menu :menuItems="$store.state.menuItems"></cac-menu>
    </footer>
  </section>
</template>

<script>
// @ is an alias to /src
// import HelloWorld from '@/components/HelloWorld.vue'
import CacHeader from '@/components/cac-components/CacHeader.vue';
import CacMenu from '@/components/cac-components/CacMenu.vue';
import BannerFullWidth from '@/components/BannerFullWidth.vue';
import ContentBlock from '@/components/ContentBlock.vue';
import ButtonSequenced from '@/components/ButtonSequenced.vue';
import AccordionIcon from '@/components/AccordionIcon.vue';
import Accordion from '@/components/Accordion.vue';

// Quiz stuff
import QuizRadio from '@/components/QuizRadio.vue';
import QuizCheckbox from '@/components/QuizCheckbox.vue';

export default {
  name: 'nccp',
  components: {
    CacHeader,
    CacMenu,
    BannerFullWidth,
    ContentBlock,
    ButtonSequenced,
    AccordionIcon,
    Accordion,
    QuizRadio
  },
  data() {
    return {
      showModal: false,
      // 3-01-03
      q01: [
        // Q1
        {
          id: 0,
          questionText: `
          <p class="text-key-take">You—the bow person in a quad—and your crew started training at 4 pm. At 4:30 pm the weather changes quickly. High winds cause whitecaps, and water is coming into the shell. You are over 5 km away from the club.</p>
        <p class="text-key-take">Which is your safest option?</p>
          `,
          questionArray: [
            'Row back to the club immediately.',
            'Call the club to request a tow from a safety boat.',
            'Head to the closest safe haven and call the club when safe.',
            'Leave the shell if filled with water, then swim to the nearest shore.'
          ],
          corrective: 'Head to the closest safe haven and call the club when safe.',
          model: [],
          answer: '',
          answerText: `
            <p>Rowing 5 km during a storm may allow too much water into your shell. Taking shelter in a safe haven is best; it is up to you to identify them before rowing. Never leave your shell; even if filled with water or capsized, it’s a helpful flotation device.</p>
          `,
        },
      ],
    };
  },
  methods: {
    tabContent(index) {
      this.tabIndex = index;
    },
  },
};
</script>

<style lang="scss" scoped>
/*------------------------------------*\
    #PRIMARY COLOURS
  \*------------------------------------*/

/*------------------------------------*\
    #Mixins
  \*------------------------------------*/

/*------------------------------------
    #MEDIA SCREENS < 768px (#MOBILE FIRST)
  \*------------------------------------*/
.banner {
  // background-image: url('../assets/img/_chapter-headers/mbl/01-welcome-mbl.png');
  background-image: url("../assets/img/_chapter-headers/mbl/2-risk-assessment-mbl.jpg");
  color: #000;
  background-position: center;
  padding: 60% 10%;
  &__header {
    color: #000;
  }
}

/*------------------------------------*\
    #MEDIA SCREENS > 576px (TABLETS)
  \*------------------------------------*/
@media (min-width: 576px) {
  .banner {
    background-image: url("../assets/img/_chapter-headers/dsk/2-risk-assessment-dsk.jpg");
    // background-image: url('../assets/img/_chapter-headers/dsk/01-welcome-dsk.png');
    color: #000;
    padding: 10% 10% 14%;
    background-position: center;
    &__header {
      color: #000;
    }
  }
}

/*------------------------------------*\
  #MEDIA SCREENS > 767px (TABLETS)
\*------------------------------------*/
@media (min-width: 767px) {
  // .banner {
  //   background-image: url('../assets/img/_bg/1-01-01-bg-dsk.png');
  //   color: #fff;
  //   padding: 10% 10%;
  //   background-position: center;
  //   &__header {
  //       color: #fff;
  //   }
  // }
}

/*------------------------------------*\
    #MEDIA SCREENS > 992px
    (LAPTOP, TABLET)
  \*------------------------------------*/
@media (min-width: 992px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1200px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1200px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1320px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1320px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1600px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1600px) {
}
</style>
